/**
 * File: useAuthMutations.ts
 * Path: apps/admin/src/modules/auth/hooks/useAuthMutations.ts
 * Description: Unified authentication mutations focused on direct login and password management.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ADMIN_PATHS } from "@idk/config";
import { AuthService } from "@/modules/auth/authService";
import { toast } from "@idk/ui";

export const useAuthMutations = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /**
   * Every mutation here reports its failure somewhere. This hook had four
   * mutations and no `onError` at all, which was survivable for login only
   * because LoginForm renders `error` inline — and not survivable for logout,
   * fired from a nav item with no error surface anywhere near it: a failed
   * sign-out did nothing at all and said nothing about it.
   *
   * `toast` is false for the two the login form already shows, so the same
   * failure is not reported twice on the same screen.
   */
  // Typed `Error`, not `unknown`: react-query infers each mutation's TError
  // from this callback, and widening it to `unknown` widens the `error` this
  // hook returns — which LoginForm renders.
  const reportError =
    (action: string, { toastIt }: { toastIt: boolean }) =>
    (error: Error) => {
      console.error(`[Auth] ${action} failed:`, error);
      if (!toastIt) return;
      toast.error(error.message || `Could not ${action}.`);
    };

  const loginMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      AuthService.login(email, password),
    onSuccess: () => {
      navigate(ADMIN_PATHS.dashboard, { replace: true });
    },
    // LoginForm renders `error` beneath the fields.
    onError: reportError("sign in", { toastIt: false }),
  });

  const googleLoginMutation = useMutation({
    mutationFn: () => AuthService.loginWithGoogle(),
    onSuccess: () => {
      navigate(ADMIN_PATHS.dashboard, { replace: true });
    },
    onError: reportError("sign in with Google", { toastIt: false }),
  });

  const logoutMutation = useMutation({
    mutationFn: () => AuthService.logout(),
    onSuccess: () => {
      queryClient.clear();
      navigate(ADMIN_PATHS.login, { replace: true });
    },
    // Fired from the sidebar and the mobile nav, neither of which reads
    // `error`: without this the button simply did nothing.
    onError: reportError("sign out", { toastIt: true }),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (email: string) => AuthService.resetPassword(email),
    onError: reportError("send the reset email", { toastIt: true }),
  });

  return {
    // Actions
    login: loginMutation.mutate,
    loginWithGoogle: googleLoginMutation.mutate,
    logout: logoutMutation.mutate,
    requestPasswordReset: resetPasswordMutation.mutate,

    // Status
    isPending:
      loginMutation.isPending ||
      googleLoginMutation.isPending ||
      logoutMutation.isPending ||
      resetPasswordMutation.isPending,

    // Errors
    error:
      loginMutation.error ||
      googleLoginMutation.error ||
      logoutMutation.error ||
      resetPasswordMutation.error,
  };
};
